import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { UserContext } from "../../context/UserContext";
import { Box, Button, CircularProgress, InputAdornment, LinearProgress, Pagination, TextField, Typography } from "@mui/material";
import { Close, FileDownload, FileUpload, Search } from "@mui/icons-material";

import { COLOR_CONSTANTS } from "../../helpers/Colors";
import { downloadPaymentTemplate, useFactoryFacilityIds, usePaymentQueue, usePaymentSummary } from "./api/paymentQueries";
import { useApprovePayment, useBulkActionPayment, useRejectPayment } from "./api/paymentMutations";
import type { BulkActionResult } from "./api/paymentMutations";
import { paymentKeys } from "./api/queryKeys";
import { PAYMENT_STATUS } from "./types/common";
import { getDefaultPaymentFilterStatus, getPaymentSidebarFilters } from "./constants/sidebarFilters";
import { PAYMENT_FILTER_DATA } from "./constants/filters";
import { useToast } from "../InvoiceManagement/hooks/useToast";
import StatusFilter from "../InvoiceManagement/components/StatusFilter";
import { WithPermission } from "../InvoiceManagement/components/WithPermission";
import { BOUNCER_PERMISSIONS } from "../../constants/bouncerPermissions";
import PaymentStatusFilterSidebar from "./components/PaymentStatusFilterSidebar";
import PaymentTable from "./components/PaymentTable";
// import ReadyToBatchCard from "./components/ReadyToBatchCard"; // not rendered — batch processing is automatic on the BE
import PaymentDetailModal from "./modals/PaymentDetailModal";
import BulkPaymentUploadModal from "./modals/BulkPaymentUploadModal";
import RejectModal from "../InvoiceManagement/v2/modals/RejectModal";
import type { PaymentQueueParams, PaymentSummaryParams, PaymentRequest } from "./types/payment";

const PAGE_SIZE = 20;

export default function PaymentList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailId, setDetailId] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<PaymentRequest | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const approveMutation = useApprovePayment();
  const rejectMutation = useRejectPayment();
  const bulkMutation = useBulkActionPayment();

  // Factory filter — mirrors v2 InvoiceList pattern.
  // invoiceFactory.item_code is a factory code string (e.g. "FCT_123", "all").
  // The payments API takes numeric vendor_facility_id(s), so we resolve the
  // factory code → facility IDs via a cached query before fetching the list.
  const { invoiceFactory } = useContext(UserContext) ?? {};
  const factoryCode =
    invoiceFactory?.item_code && invoiceFactory.item_code !== "all"
      ? (invoiceFactory.item_code as string)
      : undefined;
  const facilityIdsQuery = useFactoryFacilityIds(factoryCode);
  const facilityIds = facilityIdsQuery.data; // undefined until resolved; [] means no facilities
  // When the facility lookup hard-fails we surface an error rather than silently
  // falling through to an unfiltered list or a permanent spinner.
  const facilityLookupFailed = !!factoryCode && facilityIdsQuery.isError;
  // A factory that resolves to zero facilities must NOT fall through to an unfiltered
  // fetch — that is the leak QET-557 closes. Treat it as an explicit empty result set.
  const noFacilitiesForFactory = !!factoryCode && facilityIds?.length === 0;
  // Hold queue/summary fetches until facility IDs are resolved when a factory is selected.
  // Prevents a brief flash of unfiltered results before the ID lookup completes.
  const filterReady = !factoryCode || facilityIds !== undefined;
  // enabled:false stops the *fetch* but NOT the *render* — keepPreviousData:true means the
  // hook still serves the previous (or unfiltered) cache entry while the new key has no data.
  // scopeResolving gates the rendered data so stale rows are never shown or selectable.
  const scopeResolving = !filterReady;
  const vendorFacilityParam =
    facilityIds && facilityIds.length > 0 ? facilityIds : undefined;
  // True only when the factory scope is fully settled and safe to use for exports.
  // Applied to the Download template button to prevent cross-factory template downloads
  // in the same three states that disable the queue/summary fetches.
  const factoryScopeReady = filterReady && !noFacilitiesForFactory && !facilityLookupFailed;

  // The factory selector arrives from UserContext rather than a local handler, so
  // a factory change must mirror what handleSearch / handleFilter / handleStatusChange
  // / handlePageChange all do on a result-set change: clear the selection and reset
  // to page 1. Ref-guarded so a deep-linked ?page= survives the initial mount.
  const prevFactoryCode = useRef(factoryCode);
  useEffect(() => {
    if (prevFactoryCode.current === factoryCode) return;
    prevFactoryCode.current = factoryCode;
    setSelectedIds(new Set());
    setSearchParams((prev) => { prev.set("page", "1"); return prev; });
  }, [factoryCode, setSearchParams]);

  const defaultStatus = getDefaultPaymentFilterStatus();
  // `?page=abc`, an empty `?page=`, 0 or a negative would send offset=NaN or
  // a negative offset and hide pagination; read them all as page 1.
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const status = searchParams.get("status") ?? defaultStatus;
  const ownerRef = searchParams.get("owner_reference_name") || undefined;
  const [searchInput, setSearchInput] = useState(searchParams.get("owner_reference_name") ?? "");

  // Advanced filter state — lazy-initialised from URL, same pattern as InvoiceList
  const [filter, setFilter] = useState<Record<string, unknown>>(() => {
    try {
      const raw = searchParams.get("filterData");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  // Resolve sidebar filter key → request_status value for the API
  const sidebarFilters = getPaymentSidebarFilters();
  const activeFilter = sidebarFilters.find((f) => f.status === status);
  const requestStatusParam =
    activeFilter && activeFilter.paymentStatuses.length > 0
      ? activeFilter.paymentStatuses
      : undefined;

  const summaryParams: PaymentSummaryParams = {
    ...(ownerRef && { owner_reference_name: ownerRef }),
    ...(vendorFacilityParam && { vendor_facility_id: vendorFacilityParam }),
    ...filter,
  };

  const { data, isLoading, isFetching } = usePaymentQueue(
    {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      ...(requestStatusParam && { request_status: requestStatusParam }),
      ...summaryParams,
    } as PaymentQueueParams,
    { enabled: filterReady && !noFacilitiesForFactory },
  );

  const { data: summaryData } = usePaymentSummary(summaryParams, { enabled: filterReady && !noFacilitiesForFactory });

  const requests = scopeResolving ? [] : (data?.requests ?? []);
  // Gate counts by factoryScopeReady for the same reason requests are gated by scopeResolving:
  // enabled:false suppresses the fetch but NOT the cache read, so summaryData may still return
  // the All-Factories entry (same key when vendorFacilityParam is undefined). Without this gate
  // the sidebar counts, header total and pagination show cross-factory numbers next to an empty state.
  const allPaymentsCount = factoryScopeReady ? (summaryData?.total ?? 0) : 0; // all-status total — drives sidebar "All payments" bucket
  const statusCounts = factoryScopeReady ? (summaryData?.counts ?? {}) : {};
  // data.count = records on the current page (≤ PAGE_SIZE), not the total matching count.
  // Derive the filtered total from the summary instead: sum the relevant status buckets,
  // or fall back to the all-status total when no status filter is active.
  const totalCount = requestStatusParam && requestStatusParam.length > 0
    ? requestStatusParam.reduce((sum, s) => sum + (statusCounts[s] ?? 0), 0)
    : allPaymentsCount;
  // If summary is unavailable but a full page of rows loaded, keep "next" reachable
  // so the user isn't silently stuck on page 1.
  const totalPages = summaryData == null && requests.length === PAGE_SIZE
    ? page + 1
    : Math.ceil(totalCount / PAGE_SIZE);

  // Stale ?page= past the end → page 1, replacing the entry so Back isn't trapped; totalPages is 0 until the summary loads.
  useEffect(() => {
    if (totalPages >= 1 && page > totalPages) {
      setSearchParams((prev) => { prev.set("page", "1"); return prev; }, { replace: true });
    }
  }, [totalPages, page, setSearchParams]);

  const handleSearch = useCallback(() => {
    setSelectedIds(new Set());
    setSearchParams((prev) => {
      if (searchInput.trim()) {
        prev.set("owner_reference_name", searchInput.trim());
      } else {
        prev.delete("owner_reference_name");
      }
      prev.set("page", "1");
      return prev;
    });
  }, [searchInput, setSearchParams]);

  const handleFilter = useCallback((filterState: Record<string, unknown>) => {
    const cleaned = Object.fromEntries(
      Object.entries(filterState).filter(([, v]) =>
        Array.isArray(v) ? v.length > 0 : v !== "" && v != null
      )
    );
    setFilter(cleaned);
    setSelectedIds(new Set());
    setSearchParams((prev) => {
      if (Object.keys(cleaned).length > 0) {
        prev.set("filterData", JSON.stringify(cleaned));
      } else {
        prev.delete("filterData");
      }
      prev.set("page", "1");
      return prev;
    });
  }, [setSearchParams]);

  const handleStatusChange = (newStatus: string | null) => {
    setSelectedIds(new Set());
    setSearchParams((prev) => {
      if (newStatus == null) {
        prev.delete("status");
      } else {
        prev.set("status", newStatus);
      }
      prev.set("page", "1");
      return prev;
    });
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setSelectedIds(new Set());
    setSearchParams((prev) => {
      prev.set("page", String(newPage));
      return prev;
    });
  };

  const handleRowClick = (request: PaymentRequest) => {
    setDetailId(request.id);
  };

  const handleRowApprove = (request: PaymentRequest) => {
    approveMutation.mutate(request.id, {
      onSuccess: () => {
        setSelectedIds((prev) => { const s = new Set(prev); s.delete(request.id); return s; });
        showToast({ type: "success", message: "Payment approved" });
        queryClient.invalidateQueries(paymentKeys.all);
      },
      onError: (error: any) => {
        showToast({ type: "error", message: error?.message ?? "Failed to approve payment" });
      },
    });
  };

  const handleBulkApprove = () => {
    bulkMutation.mutate(
      { request_ids: [...selectedIds], action: "APPROVE" },
      {
        onSuccess: (result: BulkActionResult) => {
          const msg =
            result.failed.length === 0
              ? `${result.succeeded.length} payment(s) approved`
              : `${result.succeeded.length} approved, ${result.failed.length} failed`;
          showToast({ type: result.failed.length === 0 ? "success" : "error", message: msg });
          setSelectedIds(new Set());
          queryClient.invalidateQueries(paymentKeys.all);
        },
        onError: (error: any) => {
          showToast({ type: "error", message: error?.message ?? "Bulk approve failed" });
        },
      }
    );
  };

  // Unified reject handler for both single-row and bulk reject.
  const handleConfirmReject = (reason: string) => {
    if (rejectTarget) {
      rejectMutation.mutate(
        { id: rejectTarget.id, reason },
        {
          onSuccess: () => {
            setSelectedIds((prev) => { const s = new Set(prev); s.delete(rejectTarget.id); return s; });
            setRejectTarget(null);
            showToast({ type: "success", message: "Payment rejected" });
            queryClient.invalidateQueries(paymentKeys.all);
          },
          onError: (error: any) => {
            showToast({ type: "error", message: error?.message ?? "Failed to reject payment" });
          },
        }
      );
    } else if (bulkRejectOpen) {
      bulkMutation.mutate(
        { request_ids: [...selectedIds], action: "REJECT", reason },
        {
          onSuccess: (result: BulkActionResult) => {
            setBulkRejectOpen(false);
            const msg =
              result.failed.length === 0
                ? `${result.succeeded.length} payment(s) rejected`
                : `${result.succeeded.length} rejected, ${result.failed.length} failed`;
            showToast({ type: result.failed.length === 0 ? "success" : "error", message: msg });
            setSelectedIds(new Set());
            queryClient.invalidateQueries(paymentKeys.all);
          },
          onError: (error: any) => {
            showToast({ type: "error", message: error?.message ?? "Bulk reject failed" });
          },
        }
      );
    }
  };

  const handleDownloadTemplate = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const result = await downloadPaymentTemplate(summaryParams);
      // Belt-and-braces: if X-Truncated wasn't readable (gateway missing
      // Access-Control-Expose-Headers — TODO: confirm with BE/platform before merge),
      // catch the unambiguous zero-row case: we got an empty export but summary says
      // APPROVED payments exist. Avoids false positives from a narrower BE export
      // (e.g. only rows with bank accounts, only unbatched) until the exact contract
      // is confirmed.
      let truncated = result.truncated;
      if (!truncated && summaryData != null) {
        try {
          const text = await result.blob.text();
          const dataRows = text.split("\n").filter((l) => l.trim().length > 0).length - 1;
          const approvedCount = (statusCounts as Record<string, number>)[PAYMENT_STATUS.APPROVED] ?? 0;
          if (dataRows <= 0 && approvedCount > 0) truncated = true;
        } catch { /* ignore — X-Truncated remains the primary signal */ }
      }
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      if (truncated) {
        showToast({
          type: "error",
          message: result.truncated
            ? "Download is incomplete — not all matching rows were exported. Narrow your filters and try again."
            : "No rows were exported even though approved payments match these filters. Please retry, or contact support if it persists.",
        });
      }
    } catch (err: any) {
      showToast({ type: "error", message: err?.message ?? "Failed to download template" });
    } finally {
      setIsDownloading(false);
    }
  };


  return (
    <>
      {/* ReadyToBatchCard intentionally not rendered — batch processing is now automatic on the BE.
          Import is commented out above. Component + test suite retained pending product confirmation;
          delete both once confirmed that BE batching is permanent. */}

      <Box sx={{ display: "flex", gap: 2, px: 2, pt: 2, alignItems: "flex-start", flex: 1, minHeight: 0 }}>
        {/* Left sidebar — per-status counts come from /queue/summary */}
        <PaymentStatusFilterSidebar
          statusCounts={statusCounts}
          totalCount={allPaymentsCount}
          selectedStatus={status}
          onStatusChange={handleStatusChange}
        />

        {/* Main content */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden", alignSelf: "stretch" }}>
          {/* Toolbar */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" gap={1} alignItems="center">
              <Typography fontWeight={700} fontSize="16px" lineHeight="24px">
                Payment Requests
              </Typography>
              <Typography fontSize="12px" lineHeight="16px" color="text.secondary">
                ({totalCount})
              </Typography>
              <StatusFilter
                filterData={PAYMENT_FILTER_DATA}
                applyFilter={handleFilter}
                selectedFilters={filter}
                cities={[]}
                citiesLoading={false}
              />
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              <TextField
                size="small"
                placeholder="Search by reference"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                sx={{ width: 320, backgroundColor: "#fff" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18, color: "#999" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <Close
                        sx={{ fontSize: 16, color: "#999", cursor: "pointer" }}
                        onClick={() => { setSearchInput(""); setSearchParams((prev) => { prev.delete("owner_reference_name"); prev.set("page", "1"); return prev; }); }}
                      />
                    </InputAdornment>
                  ) : null,
                }}
              />
              <WithPermission permission={BOUNCER_PERMISSIONS.PAYMENT_UPLOAD}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={isDownloading || !factoryScopeReady}
                  startIcon={isDownloading ? <CircularProgress size={12} /> : <FileDownload fontSize="small" />}
                  onClick={handleDownloadTemplate}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    height: 36,
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                >
                  Download template
                </Button>
              </WithPermission>
              <WithPermission permission={BOUNCER_PERMISSIONS.PAYMENT_UPLOAD}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FileUpload fontSize="small" />}
                  onClick={() => setUploadModalOpen(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    height: 36,
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                >
                  Upload Payments
                </Button>
              </WithPermission>
            </Box>
          </Box>

          {/* Bulk action bar — inline above table, matches OrderDetails/InvoiceList pattern */}
          {selectedIds.size > 0 && (
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Typography fontSize="14px" fontWeight={600}>
                {selectedIds.size} selected
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={bulkMutation.isLoading ? <CircularProgress size={12} /> : undefined}
                disabled={bulkMutation.isLoading}
                onClick={handleBulkApprove}
                sx={{ backgroundColor: "#3655F2", textTransform: "none", px: "15px", py: "8px" }}
              >
                Approve selected
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={bulkMutation.isLoading}
                onClick={() => setBulkRejectOpen(true)}
                sx={{ textTransform: "none", px: "15px", py: "8px" }}
              >
                Reject selected
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => setSelectedIds(new Set())}
                sx={{ textTransform: "none" }}
              >
                Clear
              </Button>
            </Box>
          )}

          {/* Refetch indicator — pinned above scroll container like OrderDetails/InvoiceList */}
          {isFetching && data && (
            <LinearProgress sx={{ mb: 1, borderRadius: "4px" }} />
          )}

          <Box sx={{ flex: 1, minHeight: 0, width: "100%", overflowY: "auto", overflowX: "auto", bgcolor: "background.default", borderRadius: "8px" }}>
            <Box sx={{ width: "100%", bgcolor: "background.paper" }}>
            {/* Table — or error/empty state when factory facility lookup failed / resolved to zero */}
          {facilityLookupFailed ? (
            <Box textAlign="center" py={6}>
              <Typography color="error">
                Could not load factory data. Please refresh the page and try again.
              </Typography>
            </Box>
          ) : noFacilitiesForFactory ? (
            <Box textAlign="center" py={6}>
              <Typography color="text.secondary">
                No payment facilities found for the selected factory.
              </Typography>
            </Box>
          ) : (
              <Box sx={{ opacity: isFetching && data ? 0.5 : 1, transition: "opacity 0.15s" }}>
                <PaymentTable
                  requests={requests}
                  isLoading={isLoading || scopeResolving}
                  onRowClick={handleRowClick}
                  onApprove={handleRowApprove}
                  onReject={(request) => setRejectTarget(request)}
                  selectedIds={selectedIds}
                  onSelectionChange={setSelectedIds}
                  actionPending={approveMutation.isLoading || rejectMutation.isLoading}
                />
              </Box>
          )}
            </Box>
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="flex-end" py={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                variant="outlined"
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root.Mui-selected": {
                    color: COLOR_CONSTANTS["on-primary/variant"],
                    backgroundColor: COLOR_CONSTANTS["primary/variant"],
                    borderColor: COLOR_CONSTANTS["on-primary/variant"],
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <PaymentDetailModal
        open={detailId !== null}
        paymentId={detailId}
        onClose={() => setDetailId(null)}
        onActioned={(actionedId) => setSelectedIds((prev) => { const s = new Set(prev); s.delete(actionedId); return s; })}
      />

      <BulkPaymentUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
      />

      <RejectModal
        open={rejectTarget !== null || bulkRejectOpen}
        loading={rejectMutation.isLoading || bulkMutation.isLoading}
        onClose={() => {
          setRejectTarget(null);
          setBulkRejectOpen(false);
        }}
        onConfirm={handleConfirmReject}
      />
    </>
  );
}
